import { useState, useMemo, useEffect } from 'react';
import {
  LogOut,
  ChevronDown,
  TrendingUp,
  Info,
  Database,
  Utensils,
  Trophy,
  Target,
  Users,
} from 'lucide-react';

// Utils
import { logger } from '../../utils/logger';
import { calculateConservativeRating } from '../../utils/rating';
import { useToast } from '../../hooks/useToast';

// Chart components
import SkillDistributionChart from '../charts/SkillDistributionChart';
import TrendChart from '../charts/TrendChart';

// UI components
import Card from '../ui/Card';
import PlayerAvatar from '../ui/PlayerAvatar';
import TimeRangeFilter from '../filters/TimeRangeFilter';
import HeadToHeadCard from '../analytics/HeadToHeadCard';

// Filter utilities
import {
  filterMatchesByTimeRange,
  filterPlayerStatsByTimeRange,
} from '../../utils/filters/timeRangeFilter';

// Chemistry calculations
import { calculatePlayerTeamStats } from '../../utils/calculations/playerTeamStats';

// Bread calculations
import {
  calculateLifetimeTotal,
  calculateOutstandingDebt,
} from '../../utils/calculations/breadCalculations';

const AnalyticsTab = ({
  selectedPlayer,
  setSelectedPlayer,
  comparePlayer,
  setComparePlayer,
  players,
  playerStats,
  matches,
  handleMarkPaid,
  isAdmin = false,
}) => {
  const { addToast } = useToast();
  // Time range filter state
  const [timeRange, setTimeRange] = useState('all');

  // Filter to only show players who have played at least one match
  // This matches the Leaderboard behavior
  const activePlayers = useMemo(() => {
    return players.filter(p => (p.wins + p.losses) > 0);
  }, [players]);

  // Sort players by Conservative Rating (TrueSkill) for consistent display order
  const sortedPlayers = useMemo(() => {
    return [...activePlayers]
      .map(p => {
        const stats = playerStats[p.id];
        return stats ? { ...p, ...stats } : p;
      })
      .sort((a, b) => {
        const ratingA = calculateConservativeRating(a.mu, a.sigma);
        const ratingB = calculateConservativeRating(b.mu, b.sigma);
        return ratingB - ratingA;
      });
  }, [activePlayers, playerStats]);

  // Wrapper for handleMarkPaid to show success notification
  const handleMarkPaidWithNotification = async (playerId, type) => {
    try {
      await handleMarkPaid(playerId, type);
      const playerName = players.find(p => p.id === playerId)?.name || 'Player';
      addToast(`${playerName} ate ${type === 'full' ? 'full' : 'half'} slice!`);
    } catch (error) {
      logger.error('Error marking payment:', error);
      addToast('Failed to mark payment. Please try again.', 'error');
    }
  };

  // Filter data based on time range
  const filteredMatches = useMemo(() => {
    const filtered = filterMatchesByTimeRange(matches, timeRange);
    logger.debug(
      `Time range: ${timeRange}, Original matches: ${
        matches?.length || 0
      }, Filtered: ${filtered?.length || 0}`
    );
    return filtered;
  }, [matches, timeRange]);

  const filteredPlayerStats = useMemo(() => {
    const filtered = filterPlayerStatsByTimeRange(
      playerStats,
      filteredMatches,
      players
    );
    logger.debug(
      'Filtered player stats:',
      Object.values(filtered).map(p => ({
        name: p.name,
        originalMu: players.find(op => op.id === p.id)?.mu,
        filteredMu: p.mu,
        originalSigma: players.find(op => op.id === p.id)?.sigma,
        filteredSigma: p.sigma,
      }))
    );
    return filtered;
  }, [playerStats, filteredMatches, players]);

  // Memoize expensive team stats calculation
  const teamStats = useMemo(() => {
    if (!selectedPlayer) return [];
    return calculatePlayerTeamStats(selectedPlayer, players, matches);
  }, [selectedPlayer, players, matches]);

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
      {!selectedPlayer ? (
        // ROSTER SELECTOR / LEAGUE OVERVIEW
        <div className="space-y-8">
          {/* Leaderboard Highlights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6 bg-zinc-50">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-4 h-4 text-amber-500" />
                <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                  Top Rated
                </span>
              </div>
              {sortedPlayers
                .slice(0, 3)
                .map((p, i) => (
                  <div
                    key={p.id}
                    className="flex justify-between items-center mb-2 last:mb-0
                      cursor-pointer hover:bg-zinc-100 p-2 rounded transition-colors"
                    onClick={() => setSelectedPlayer(p)}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-bold w-4 ${
                          i === 0 ? 'text-zinc-400' : 'text-zinc-400'
                        }`}
                      >
                        #{i + 1}
                      </span>
                      <PlayerAvatar initials={p.initials} size="sm" />
                      <span className="text-sm font-bold text-zinc-800">
                        {p.name}
                      </span>
                    </div>
                    <span className="font-mono text-xs font-bold text-amber-600">
                      {calculateConservativeRating(p.mu, p.sigma).toFixed(2)}
                    </span>
                  </div>
                ))}
            </Card>
            <Card className="p-6 bg-zinc-50">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                  Win Rate Leaders
                </span>
              </div>
              {sortedPlayers
                .map(p => ({
                  ...p,
                  wr: p.wins + p.losses > 0 ? p.wins / (p.wins + p.losses) : 0,
                }))
                .sort((a, b) => b.wr - a.wr)
                .slice(0, 3)
                .map((p, i) => (
                  <div
                    key={p.id}
                    className="flex justify-between items-center mb-2 last:mb-0
                      cursor-pointer hover:bg-zinc-100 p-2 rounded transition-colors"
                    onClick={() => setSelectedPlayer(p)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold w-4 text-zinc-400">
                        #{i + 1}
                      </span>
                      <PlayerAvatar initials={p.initials} size="sm" />
                      <span className="text-sm font-bold text-zinc-800">
                        {p.name}
                      </span>
                    </div>
                    <span className="font-mono text-xs font-bold text-emerald-600">
                      {(p.wr * 100).toFixed(0)}%
                    </span>
                  </div>
                ))}
            </Card>
            <Card className="p-6 bg-zinc-50">
              <div className="flex items-center gap-2 mb-4">
                <Utensils className="w-4 h-4 text-red-500" />
                <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                  Outstanding Bread Debt
                </span>
              </div>
              {Object.values(playerStats)
                .map(p => ({
                  ...p,
                  totalOutstandingSlices: calculateOutstandingDebt(p).total,
                }))
                .filter(p => p.totalOutstandingSlices > 0) // Only show players with actual debt
                .sort((a, b) => b.totalOutstandingSlices - a.totalOutstandingSlices)
                .slice(0, 3)
                .map((p, i) => (
                  <div
                    key={p.id}
                    className="flex justify-between items-center mb-2 last:mb-0
                      cursor-pointer hover:bg-zinc-100 p-2 rounded transition-colors"
                    onClick={() => setSelectedPlayer(p)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold w-4 text-zinc-400">
                        #{i + 1}
                      </span>
                      <PlayerAvatar initials={p.initials} size="sm" />
                      <span className="text-sm font-bold text-zinc-800">
                        {p.name}
                      </span>
                    </div>
                    <span className="font-mono text-xs font-bold text-red-600">
                      {p.totalOutstandingSlices.toFixed(1)} slices
                    </span>
                  </div>
                ))}
            </Card>
          </div>

          {/* Complete Roster */}
          <div>
            <div className="mb-4">
              <h2 className="text-lg font-bold tracking-tight text-zinc-900">
                Select Player for Deep Dive
              </h2>
              <p className="text-xs text-zinc-400 mt-1">
                Only players with matches are shown
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {sortedPlayers.map(p => (
                <Card
                  key={p.id}
                  className="p-4 cursor-pointer hover:border-zinc-400 transition-all group shadow-none hover:shadow-md"
                  onClick={() => setSelectedPlayer(p)}
                >
                  <div className="flex items-center gap-3">
                    <PlayerAvatar initials={p.initials} size="md" />
                    <div>
                      <div className="font-bold text-sm text-zinc-900 group-hover:text-indigo-600">
                        {p.name}
                      </div>
                      <div className="text-xs text-zinc-500 font-mono">
                        Rating:{' '}
                        {calculateConservativeRating(p.mu, p.sigma).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      ) : (
        // DETAILED ANALYTICS
        <div>
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={() => {
                setSelectedPlayer(null);
                setComparePlayer(null);
              }}
              className="text-xs font-bold text-zinc-400 hover:text-zinc-900 flex items-center gap-2 transition-colors"
            >
              <LogOut className="w-3 h-3 rotate-180" /> BACK TO OVERVIEW
            </button>

            <div className="flex items-center gap-4">
              <TimeRangeFilter
                timeRange={timeRange}
                setTimeRange={setTimeRange}
                matches={matches}
              />

              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase text-zinc-400">
                  Compare:
                </span>
                <div className="relative">
                  <select
                    className="appearance-none bg-white border border-zinc-200 text-sm rounded px-3 py-1.5 pr-8 outline-none focus:border-zinc-900 text-zinc-700 shadow-sm"
                    onChange={e =>
                      setComparePlayer(
                        players.find(p => p.id === e.target.value) || null
                      )
                    }
                    value={comparePlayer?.id || ''}
                  >
                    <option value="">Select Rival...</option>
                    {sortedPlayers
                      .filter(p => p.id !== selectedPlayer.id)
                      .map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                  </select>
                  <ChevronDown className="w-3 h-3 absolute right-2 top-2 text-zinc-400 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* SKILL CURVE */}
              <Card className="p-8 relative overflow-hidden">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-5">
                    <PlayerAvatar
                      initials={selectedPlayer.initials}
                      size="lg"
                      inverted
                    />
                    <div>
                      <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">
                        {selectedPlayer.name}
                      </h1>
                      <p className="text-zinc-500 text-sm mt-1 font-medium">
                        TrueSkill™:{' '}
                        <span className="text-zinc-900 font-mono">
                          {calculateConservativeRating(
                            selectedPlayer.mu,
                            selectedPlayer.sigma
                          ).toFixed(2)}
                        </span>
                      </p>
                    </div>
                  </div>
                  {comparePlayer && (
                    <div className="flex items-center gap-3 opacity-60">
                      <div className="text-right">
                        <h2 className="text-sm font-bold text-zinc-900">
                          {comparePlayer.name}
                        </h2>
                        <p className="font-mono text-xs">
                          {calculateConservativeRating(
                            comparePlayer.mu,
                            comparePlayer.sigma
                          ).toFixed(2)}
                        </p>
                      </div>
                      <PlayerAvatar
                        initials={comparePlayer.initials}
                        size="md"
                        color="#71717a"
                      />
                    </div>
                  )}
                </div>
                <SkillDistributionChart
                  player={
                    filteredPlayerStats[selectedPlayer.id] || selectedPlayer
                  }
                  comparison={
                    comparePlayer
                      ? filteredPlayerStats[comparePlayer.id] || comparePlayer
                      : null
                  }
                  allPlayers={Object.values(filteredPlayerStats)}
                />
              </Card>

              {/* PERFORMANCE TREND */}
              <Card className="py-4 px-2">
                <div className="flex justify-between items-center mb-4 px-4">
                  <h3 className="font-bold text-xs uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                    <TrendingUp className="w-3 h-3" /> Performance History
                  </h3>
                  <div className="flex gap-3 text-[10px] font-bold">
                    <span className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-zinc-900"></div>{' '}
                      {selectedPlayer.name}
                    </span>
                    {comparePlayer && (
                      <span className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-zinc-300"></div>{' '}
                        {comparePlayer.name}
                      </span>
                    )}
                  </div>
                </div>
                <div className="h-48 w-full">
                  <TrendChart
                    history={
                      filteredPlayerStats[selectedPlayer.id]?.history || []
                    }
                    comparisonHistory={
                      comparePlayer
                        ? filteredPlayerStats[comparePlayer.id]?.history
                        : null
                    }
                    player={selectedPlayer}
                    matches={filteredMatches}
                    comparePlayer={comparePlayer}
                  />
                </div>
              </Card>

              {/* PLAYER CHEMISTRY */}
              <Card className="p-6 bg-zinc-50 border-zinc-200">
                <div className="flex items-center gap-2 mb-3 text-zinc-900">
                  <Users className="w-4 h-4" />
                  <h3 className="text-xs font-bold uppercase tracking-widest">
                    Team Chemistry
                  </h3>
                </div>
                <div className="space-y-3">
                  {teamStats.map(
                    ({
                      partner,
                      chemistry,
                      teamMatches,
                      teamWins,
                      teamLosses,
                      winRate,
                    }) => (
                      <div
                        key={partner.id}
                        className="flex items-center justify-between p-3 bg-white rounded border border-zinc-200 hover:border-zinc-300 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <PlayerAvatar initials={partner.initials} size="sm" />
                          <div>
                            <button
                              onClick={() => setSelectedPlayer(partner)}
                              className="text-sm font-bold text-zinc-900 hover:text-indigo-600 transition-colors cursor-pointer text-left"
                            >
                              {partner.name}
                            </button>
                            <div className="text-xs text-zinc-500">
                              {teamMatches.length > 0 ? (
                                <span>
                                  {teamMatches.length} games together /{' '}
                                  {teamWins}W-{teamLosses}L
                                </span>
                              ) : (
                                <span>No games together</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {teamMatches.length > 0 && (
                            <div className="text-right">
                              <div className="text-xs text-zinc-500 font-medium">
                                Win Rate
                              </div>
                              <div
                                className={`text-lg font-bold font-mono ${
                                  winRate >= 70
                                    ? 'text-emerald-600'
                                    : winRate >= 50
                                      ? 'text-amber-600'
                                      : 'text-red-600'
                                }`}
                              >
                                {winRate}%
                              </div>
                            </div>
                          )}
                          <div className="text-right">
                            <div className="text-xs text-zinc-500 font-medium">
                              Chemistry
                            </div>
                            <div
                              className={`text-lg font-bold font-mono ${
                                chemistry >= 75
                                  ? 'text-emerald-600'
                                  : chemistry >= 60
                                    ? 'text-amber-600'
                                    : chemistry >= 40
                                      ? 'text-zinc-700'
                                      : 'text-red-600'
                              }`}
                            >
                              {chemistry}%
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                  {players.filter(p => p.id !== selectedPlayer.id).length ===
                    0 && (
                    <div className="text-center text-zinc-400 text-sm italic py-4">
                      No other players available
                    </div>
                  )}
                </div>
                <div className="mt-4 pt-3 border-t border-zinc-200">
                  <div className="text-xs text-zinc-500 leading-relaxed">
                    <strong>Chemistry Score:</strong> Combines win rate (70%)
                    and score margins (30%) from actual games together. When no
                    games exist, uses neural network prediction based on
                    individual skill ratings.
                  </div>
                </div>
              </Card>
            </div>

            {/* SIDEBAR */}
            <div className="space-y-6">
              {/* HEAD TO HEAD (Conditional) */}
              {comparePlayer && (
                <HeadToHeadCard
                  selectedPlayer={selectedPlayer}
                  comparePlayer={comparePlayer}
                  filteredMatches={filteredMatches}
                />
              )}

              {/* ALGORITHM CONFIDENCE */}
              <Card className="p-6 bg-zinc-50 border-zinc-200">
                <div className="flex items-center gap-2 mb-3 text-zinc-900">
                  <Info className="w-4 h-4" />
                  <h3 className="text-xs font-bold uppercase tracking-widest">
                    Skill Confidence
                  </h3>
                </div>
                <ul className="space-y-2 text-[11px] text-zinc-500">
                  <li className="flex justify-between">
                    <span className="font-bold text-zinc-700">Mean (μ):</span>{' '}
                    <span>{(selectedPlayer.mu || 0).toFixed(2)}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="font-bold text-zinc-700">
                      Uncertainty (σ):
                    </span>{' '}
                    <span>{(selectedPlayer.sigma || 0).toFixed(2)}</span>
                  </li>
                  <li className="flex justify-between pt-2 border-t border-zinc-200 mt-2">
                    <span className="font-bold text-zinc-700">
                      Consistency:
                    </span>
                    <span
                      className={
                        selectedPlayer.sigma < 3
                          ? 'text-emerald-600 font-bold'
                          : 'text-amber-600'
                      }
                    >
                      {selectedPlayer.sigma < 3
                        ? 'High'
                        : selectedPlayer.sigma < 6
                          ? 'Medium'
                          : 'Low'}
                    </span>
                  </li>
                </ul>
              </Card>

              {/* RATING CALCULATION */}
              <Card className="p-6 bg-zinc-50 border-zinc-200">
                <div className="flex items-center gap-2 mb-3 text-zinc-900">
                  <Info className="w-4 h-4" />
                  <h3 className="text-xs font-bold uppercase tracking-widest">
                    Rating Calculation
                  </h3>
                </div>
                <p className="text-xs text-zinc-600 leading-relaxed mb-3">
                  We use <strong>TrueSkill</strong> with score margin weighting.
                  Blowout wins/losses count more than close games.
                </p>
                <div className="space-y-2 text-[11px] text-zinc-500">
                  <div className="flex justify-between items-center p-2 bg-white rounded border">
                    <span className="font-bold text-zinc-700">
                      Close Game (10-8):
                    </span>
                    <span className="text-zinc-500 font-mono">1.14x</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded border">
                    <span className="font-bold text-zinc-700">
                      Solid Win (10-5):
                    </span>
                    <span className="text-zinc-500 font-mono">1.35x</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded border">
                    <span className="font-bold text-zinc-700">
                      Near Shutout (10-1):
                    </span>
                    <span className="text-amber-600 font-mono font-bold">
                      2.0x
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded border">
                    <span className="font-bold text-zinc-700">
                      Shutout (10-0):
                    </span>
                    <span className="text-red-600 font-mono font-bold">
                      3.0x
                    </span>
                  </div>
                </div>
                <p className="text-[10px] text-zinc-400 mt-3 italic">
                  * Multiplier applied to base rating change. 10-0 = like losing
                  3 games.
                </p>
              </Card>

              {/* STATS */}
              <Card className="p-6 bg-zinc-50 border-zinc-200">
                <div className="flex items-center gap-2 mb-3 text-zinc-900">
                  <Database className="w-4 h-4" />
                  <h3 className="text-xs font-bold uppercase tracking-widest">
                    Overall Stats
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-2 rounded border border-zinc-200">
                    <div className="text-[10px] text-zinc-500 uppercase font-bold">
                      Win Rate
                    </div>
                    <div className="text-lg font-bold text-zinc-900">
                      {(
                        ((filteredPlayerStats[selectedPlayer.id]
                          ?.filteredWins || 0) /
                          ((filteredPlayerStats[selectedPlayer.id]
                            ?.filteredWins || 0) +
                            (filteredPlayerStats[selectedPlayer.id]
                              ?.filteredLosses || 0) || 1)) *
                        100
                      ).toFixed(0)}
                      %
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded border border-zinc-200">
                    <div className="text-[10px] text-zinc-500 uppercase font-bold">
                      Games
                    </div>
                    <div className="text-lg font-bold text-zinc-900">
                      {(filteredPlayerStats[selectedPlayer.id]?.filteredWins ||
                        0) +
                        (filteredPlayerStats[selectedPlayer.id]
                          ?.filteredLosses || 0)}
                    </div>
                  </div>
                </div>
              </Card>

              {/* BREAD STATISTICS */}
              <Card className="p-6 bg-zinc-50 border-zinc-200">
                <div className="flex items-center gap-2 mb-3 text-zinc-900">
                  <Utensils className="w-4 h-4" />
                  <h3 className="text-xs font-bold uppercase tracking-widest">
                    Bread Lifetime Records
                  </h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-2 bg-white rounded border border-zinc-200">
                    <span className="text-sm font-bold text-zinc-700">
                      🍞 Outstanding Full
                    </span>
                    <span className="font-mono font-bold text-xl text-zinc-950">
                      {
                        calculateOutstandingDebt(playerStats[selectedPlayer.id])
                          .fullSlices
                      }
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white rounded border border-zinc-200">
                    <span className="text-sm font-bold text-zinc-700">
                      🥪 Outstanding Half
                    </span>
                    <span className="font-mono font-bold text-xl text-zinc-900">
                      {
                        calculateOutstandingDebt(playerStats[selectedPlayer.id])
                          .halfSlices
                      }
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-zinc-50 rounded border border-zinc-100 text-xs">
                    <span className="font-bold text-zinc-600">
                      Lifetime Total
                    </span>
                    <span className="font-mono font-bold text-zinc-800">
                      {calculateLifetimeTotal(
                        playerStats[selectedPlayer.id]
                      ).toFixed(1)}{' '}
                      slices
                    </span>
                  </div>
                  {isAdmin && (
                    <div className="pt-3 border-t border-zinc-200 flex gap-2 justify-end">
                      {Math.max(
                        0,
                        (playerStats[selectedPlayer.id]?.earnedFullSlices ||
                          0) -
                          (playerStats[selectedPlayer.id]?.paidFullSlices || 0)
                      ) > 0 && (
                        <button
                          onClick={() =>
                            handleMarkPaidWithNotification(selectedPlayer.id, 'full')
                          }
                          className="text-[10px] font-bold text-zinc-900 bg-zinc-100 hover:bg-zinc-200 px-3 py-1 rounded transition-colors border border-zinc-200 uppercase"
                        >
                          ATE 1 FULL
                        </button>
                      )}
                      {Math.max(
                        0,
                        (playerStats[selectedPlayer.id]?.earnedHalfSlices ||
                          0) -
                          (playerStats[selectedPlayer.id]?.paidHalfSlices || 0)
                      ) > 0 && (
                        <button
                          onClick={() =>
                            handleMarkPaidWithNotification(selectedPlayer.id, 'half')
                          }
                          className="text-[10px] font-bold text-zinc-900 bg-zinc-100 hover:bg-zinc-200 px-3 py-1 rounded transition-colors border border-zinc-200 uppercase"
                        >
                          ATE 1 HALF
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsTab;
